import { TOUR_POLICY_COLOR_PATTERN, TOUR_POLICY_MAX_ITEMS, TOUR_POLICY_MIN_ITEMS, TOUR_POLICY_PERIOD_MAX_LENGTH, TOUR_POLICY_RATE_MAX_LENGTH, TOUR_REFUND_DAYS_BEFORE_MAX } from "utils/constant/tour-policy";

import type { TourPolicyItem, TourRefundPolicyInput, TourRefundPolicyItem } from "api/types/tours.types";
import type { TourPolicyFormRow, TourPolicyKind } from "utils/type/tour-policy";

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** bool·소수·문자열을 거부하는 정수 범위 검사 — BE `_is_plain_int` 와 같은 규칙 */
const isPlainIntInRange = (v: unknown, min: number, max: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;

/**
 * 저장된 규정을 표시·편집 가능한 항목 목록으로 바꾼다.
 * 과거 데이터는 목록이 아니거나 형식이 다를 수 있다 — 목록이 아니면 빈 배열, 객체가 아닌 항목은 버린다.
 */
export const normalizePolicyItems = (raw: unknown): TourRefundPolicyItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((item) => {
    const normalized: TourRefundPolicyItem = {
      period: typeof item.period === "string" ? item.period : "",
      rate: typeof item.rate === "string" ? item.rate : "",
    };
    if (typeof item.color === "string") normalized.color = item.color;
    if (typeof item.days_before === "number") normalized.days_before = item.days_before;
    if (typeof item.rate_percent === "number") normalized.rate_percent = item.rate_percent;
    return normalized;
  });
};

/**
 * 예약금 규정이 회원 직접 취소 계산에 쓸 수 있는 구조인지 — BE `_parse_structured_policy` 와 같은 판정.
 * 하나라도 `days_before`·`rate_percent` 가 없거나 범위 밖·중복이면 BE 는 취소를 막는다.
 */
export const isRefundPolicyCalculable = (raw: unknown): boolean => {
  if (!Array.isArray(raw) || raw.length === 0) return false;
  const seen = new Set<number>();
  for (const item of raw) {
    if (!isRecord(item)) return false;
    if (!isPlainIntInRange(item.days_before, 0, TOUR_REFUND_DAYS_BEFORE_MAX)) return false;
    if (!isPlainIntInRange(item.rate_percent, 0, 100)) return false;
    if (seen.has(item.days_before)) return false;
    seen.add(item.days_before);
  }
  return true;
};

export const createEmptyPolicyRow = (): TourPolicyFormRow => ({
  period: "",
  rate: "",
  color: "",
  days_before: "",
  rate_percent: "",
});

export const toPolicyFormRows = (items: TourRefundPolicyItem[]): TourPolicyFormRow[] =>
  items.map((item) => ({
    period: item.period ?? "",
    rate: item.rate ?? "",
    color: item.color ?? "",
    days_before: item.days_before == null ? "" : String(item.days_before),
    rate_percent: item.rate_percent == null ? "" : String(item.rate_percent),
  }));

/** 폼 행끼리 비교 — 바뀐 규정만 PATCH 에 싣기 위해 쓴다 (예약금은 계산 키까지, 위약금은 표시 키만) */
export const isSamePolicyRows = (a: TourPolicyFormRow[], b: TourPolicyFormRow[], kind: TourPolicyKind): boolean => {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i];
    const sameDisplay = row.period === other.period && row.rate === other.rate && row.color === other.color;
    if (kind === "penalty") return sameDisplay;
    return sameDisplay && row.days_before === other.days_before && row.rate_percent === other.rate_percent;
  });
};

const parseIntStrict = (v: string): number | null => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null);

/**
 * BE `TourPolicyField` / `TourRefundPolicyField` 와 같은 규칙으로 검증한다.
 * 문제가 없으면 null, 있으면 첫 번째 오류 문구를 돌려준다.
 */
export const validatePolicyRows = (rows: TourPolicyFormRow[], kind: TourPolicyKind): string | null => {
  if (rows.length < TOUR_POLICY_MIN_ITEMS) return `규정은 최소 ${TOUR_POLICY_MIN_ITEMS}개 항목이 필요합니다.`;
  if (rows.length > TOUR_POLICY_MAX_ITEMS) return `규정은 최대 ${TOUR_POLICY_MAX_ITEMS}개 항목까지 가능합니다.`;

  const seenDays = new Set<number>();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const no = i + 1;
    if (!row.period.trim()) return `${no}번째 항목의 기간을 입력하세요.`;
    if (row.period.trim().length > TOUR_POLICY_PERIOD_MAX_LENGTH) return `${no}번째 항목의 기간은 최대 ${TOUR_POLICY_PERIOD_MAX_LENGTH}자까지 가능합니다.`;
    if (!row.rate.trim()) return `${no}번째 항목의 환불 표시 문구를 입력하세요.`;
    if (row.rate.trim().length > TOUR_POLICY_RATE_MAX_LENGTH) return `${no}번째 항목의 환불 표시 문구는 최대 ${TOUR_POLICY_RATE_MAX_LENGTH}자까지 가능합니다.`;
    if (row.color && !TOUR_POLICY_COLOR_PATTERN.test(row.color)) return `${no}번째 항목의 색상은 #RRGGBB 형식이어야 합니다.`;

    if (kind === "refund") {
      const days = parseIntStrict(row.days_before);
      if (days == null || days > TOUR_REFUND_DAYS_BEFORE_MAX) return `${no}번째 항목의 기준일은 0 이상 ${TOUR_REFUND_DAYS_BEFORE_MAX} 이하의 정수여야 합니다.`;
      if (seenDays.has(days)) return `${no}번째 항목의 기준일(${days}일 전)이 이전 항목과 중복됩니다.`;
      seenDays.add(days);
      const percent = parseIntStrict(row.rate_percent);
      if (percent == null || percent > 100) return `${no}번째 항목의 환불율은 0 이상 100 이하의 정수여야 합니다.`;
    }
  }
  return null;
};

const toPolicyItem = (row: TourPolicyFormRow): TourPolicyItem => {
  const item: TourPolicyItem = { period: row.period.trim(), rate: row.rate.trim() };
  if (row.color) item.color = row.color.toUpperCase();
  return item;
};

/** 검증을 통과한 행만 넘긴다는 전제 (validatePolicyRows 이후 호출) */
export const toPenaltyPolicyPayload = (rows: TourPolicyFormRow[]): TourPolicyItem[] => rows.map(toPolicyItem);

export const toRefundPolicyPayload = (rows: TourPolicyFormRow[]): TourRefundPolicyInput[] =>
  rows.map((row) => ({
    ...toPolicyItem(row),
    days_before: Number(row.days_before.trim()),
    rate_percent: Number(row.rate_percent.trim()),
  }));

/**
 * 기준일 구조로 만든 예약금 기간 문구. BE 판정(출발일 − days_before일 23:59:59 까지 적용,
 * 바로 위 구간이 끝난 다음 날부터 시작)과 같은 구간을 문장으로 옮긴다.
 *  - 가장 큰 기준일: "출발 45일 전까지"
 *  - 중간: "출발 15~29일 전" (한 날이면 "출발 15일 전")
 *  - 기준일 0: "출발 44일 전부터"
 * @param upperDays 바로 위 구간의 기준일 (없으면 null)
 */
export const buildRefundPeriodText = (days: number, upperDays: number | null): string => {
  if (upperDays == null) return days === 0 ? "출발 당일까지" : `출발 ${days}일 전까지`;
  const from = upperDays - 1;
  if (days === 0) return from === 0 ? "출발 당일" : `출발 ${from}일 전부터`;
  return days === from ? `출발 ${days}일 전` : `출발 ${days}~${from}일 전`;
};

/** 환불율로 만든 환불 표시 문구 — 0% 는 "환불 불가" */
export const buildRefundRateText = (percent: number): string => (percent === 0 ? "환불 불가" : `${percent}%`);

/** 행마다 기준일 구조 문구. 기준일이 하나라도 비었거나 범위 밖·중복이면 구간을 정할 수 없어 null */
const buildRefundPeriodTexts = (rows: TourPolicyFormRow[]): string[] | null => {
  const days = rows.map((row) => parseIntStrict(row.days_before));
  if (days.some((d) => d == null || d > TOUR_REFUND_DAYS_BEFORE_MAX)) return null;
  const values = days as number[];
  if (new Set(values).size !== values.length) return null;
  return values.map((d) => {
    const uppers = values.filter((other) => other > d);
    return buildRefundPeriodText(d, uppers.length > 0 ? Math.min(...uppers) : null);
  });
};

const DIGIT_RUN = /\d+/g;
const digitRuns = (text: string): string[] => text.match(DIGIT_RUN) ?? [];

/**
 * 이전 구조 문구(`oldGen`)에 맞춰 쓰인 문구를 새 구조 문구(`newGen`)로 옮긴다.
 *  - 문구 안에 이전 구조 문구가 그대로 있으면 그 부분만 바꾼다 ("결제 후 " 같은 앞뒤 문구 유지)
 *  - 숫자 배열이 이전 구조와 같고 개수도 같으면 숫자만 순서대로 바꾼다
 *  - 비어 있으면 새 구조 문구로 채운다
 *  - 그 밖(직접 쓴 문구)은 그대로 둔다
 */
const shiftText = (text: string, oldGen: string | null, newGen: string): string => {
  if (!text.trim()) return newGen;
  if (oldGen == null) return text;
  if (text.includes(oldGen)) return text.replace(oldGen, newGen);
  const oldNums = digitRuns(oldGen);
  const newNums = digitRuns(newGen);
  const textNums = digitRuns(text);
  const sameNums = textNums.length === oldNums.length && textNums.every((n, i) => n === oldNums[i]);
  if (!sameNums || oldNums.length !== newNums.length || oldNums.length === 0) return text;
  let idx = 0;
  return text.replace(DIGIT_RUN, () => newNums[idx++]);
};

/**
 * 기준일 변경을 기간 문구에 반영한다. 한 항목의 기준일이 바뀌면 이웃 구간의 경계도 바뀌므로 전체 행을 다시 맞춘다.
 * `base` 는 편집 시작 시점의 행(입력 중 빈 값을 거쳐도 문구가 어긋나지 않도록 매 입력마다 이 시점에서 다시 계산한다).
 * 새 기준일로 구간을 정할 수 없으면(빈 값·중복 등) 시작 시점 문구를 유지한다.
 */
export const syncRefundPeriodTexts = (base: TourPolicyFormRow[], next: TourPolicyFormRow[]): TourPolicyFormRow[] => {
  const oldTexts = buildRefundPeriodTexts(base);
  const newTexts = buildRefundPeriodTexts(next);
  return next.map((row, i) => {
    const basePeriod = base[i]?.period ?? row.period;
    if (newTexts == null) return { ...row, period: basePeriod };
    return { ...row, period: shiftText(basePeriod, oldTexts?.[i] ?? null, newTexts[i]) };
  });
};

/** 환불율 변경을 환불 문구에 반영한다 (`base` 는 편집 시작 시점의 행). 값이 비거나 범위 밖이면 시작 시점 문구 유지 */
export const syncRefundRateText = (base: TourPolicyFormRow, next: TourPolicyFormRow): TourPolicyFormRow => {
  const newPercent = parseIntStrict(next.rate_percent);
  if (newPercent == null || newPercent > 100) return { ...next, rate: base.rate };
  const oldPercent = parseIntStrict(base.rate_percent);
  const oldGen = oldPercent == null || oldPercent > 100 ? null : buildRefundRateText(oldPercent);
  const newGen = buildRefundRateText(newPercent);
  if (!base.rate.trim()) return { ...next, rate: newGen };
  return { ...next, rate: oldGen != null && base.rate.trim() === oldGen ? newGen : base.rate };
};

/** 400 응답 본문에서 규정 필드 오류 문구를 꺼낸다 (DRF: `{ refund_policy: ["..."] }`) */
export const extractPolicyFieldError = (data: unknown, fieldKey: string): string | null => {
  if (!isRecord(data)) return null;
  const value = data[fieldKey];
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return null;
};
